import { Component, OnInit } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { EventService } from "src/app/core/services/event.service";
import { AnalyticsService } from "src/app/core/services/analytics.service";
import { SafeStorage } from "src/app/core/utils/safe-storage";

@Component({
  selector: "app-splash-screen",
  templateUrl: "./splash-screen.component.html",
  styleUrls: ["./splash-screen.component.css"],
})
export class SplashScreenComponent implements OnInit {
  constructor(
    private commonservice: EventService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private bar: MatSnackBar,
    private analytics: AnalyticsService
  ) { }

  ngOnInit(): void {
    // Fire before localStorage.clear()/the auth gate below so a bounce at
    // login is still captured. This endpoint (pwa_fetch_hex_status) doesn't
    // return a numeric event_id, so guest event-context registration happens
    // later once the user reaches event-detail's own data fetch instead.
    this.analytics.capture('invite_opened', {
      entry: 'splash',
      is_authenticated: localStorage.getItem('user') != null,
    });

    // Preserve PostHog's persisted distinct_id (`ph_*`) so the anonymous
    // identity from the invite_opened capture above can still be merged once
    // the guest identifies later — a plain clear() orphans it, which is why
    // invite_opened and guest_joined never used to link to the same person.
    SafeStorage.clearExceptPrefixes(['ph_']);
    localStorage.setItem("openPopup", "true");
    // const url = new URL(window.location.href);
    // // ✅ Get "access" query param
    // let access = this.extractAccessFromUrl(url);

    // if (access === 'restricted') {
    //   localStorage.setItem('access', 'restricted');
    // }
    this.activatedRoute.queryParams.subscribe((params: { [x: string]: any; }) => {
      const token = params['a'];
      if (token) {
        try {
          const payload = JSON.parse(atob(token));
          if (payload.restricted) {
            // console.log('User is restricted for event:', payload.eventId); //__CONSOLE_LOG__
            // Save for use after login
            localStorage.setItem('access', 'restricted');
          } else {
            // console.log('User is regular'); //__CONSOLE_LOG__
          }
        } catch (err) {
          // console.error('Invalid access', err); //__CONSOLE_LOG__
        }
      }
    });
    this.setupRouteListener();
    this.detectPlatformAndRedirect();
  }

  extractAccessFromUrl(url: string | URL) {
    try {
      const hash = new URL(url).hash || "";         // "#/splash/e0362c43?access=restricted"
      const qIndex = hash.indexOf("?");
      if (qIndex === -1) return null;
      const queryString = hash.slice(qIndex + 1);   // "access=restricted"
      const params = new URLSearchParams(queryString);
      return params.get("access");                  // "restricted" or null
    } catch (e) {
      return null;
    }
  }

  /**
   * Sets up a listener for route parameters and processes the event ID.
   */
  private setupRouteListener(): void {
    this.activatedRoute.params.subscribe((params: any) => {
      const eventId = params.id;
      // console.log("Event ID from params:", eventId); //__CONSOLE_LOG__ //__CONSOLE_LOG__
      if (this.isValidEventId(eventId)) {
        this.processEventId(eventId);
      } else {
        this.handleInvalidEventId();
      }
    });
  }

  /**
   * Checks if the event ID is valid.
   * @param eventId - The event ID from the route parameters.
   * @returns True if the event ID is valid, otherwise false.
   */
  private isValidEventId(eventId: any): boolean {
    return eventId != null && eventId !== "null" && eventId !== undefined;
  }

  /**
   * Processes the event ID by fetching data and handling the response.
   * @param eventId - The event ID to process.
   */
  private processEventId(eventId: string): void {
    localStorage.setItem("eventId", eventId);
    this.commonservice.pwaFetchData(eventId).subscribe(
      (data: any) => this.handleEventData(data),
      (error: any) => this.handleError(error)
    );
  }

  /**
   * Handles invalid event IDs by navigating to the login page.
   */
  private handleInvalidEventId(): void {
    // console.log("called from splash"); //__CONSOLE_LOG__
    localStorage.setItem("user", "null");
    this.navigateToLogin();
  }

  /**
   * Handles the event data response.
   * @param data - The event data response.
   */
  private handleEventData(data: any): void {
    switch (data.statusCode) {
      case 200:
      case 201:
        this.handleSuccessfulEventData(data);
        break;
      case 202:
      case 203:
      case 204:
      case 501:
        this.handleErrorResponse(data);
        break;
      default:
        break;
    }
  }

  /**
   * Handles successful event data by setting localStorage items and navigating to login.
   * @param data - The event data response.
   */
  private handleSuccessfulEventData(data: any): void {
    // console.log({data}); //__CONSOLE_LOG__

    if (data.locked == 1 || data.locked === true) {
      this.bar.open('This Event Gallery is temporarily paused', 'Ok', {
        panelClass: 'error-bar',
        duration: 4000,
      });
      this.router.navigateByUrl('/home');
      return;
    }

    this.activatedRoute.params.subscribe((params: any) => {
      const eventId = params.id;
      // console.log("Event ID from params:", eventId); //__CONSOLE_LOG__ //__CONSOLE_LOG__
      if (eventId !== "null") {
        localStorage.setItem("eventId", eventId);
        localStorage.setItem("isEventId", "true");
        let restricted = localStorage.getItem("access") ? true : false;

        if (data.result == "live" && data.event_state == "Scheduled") {
          localStorage.setItem("showGuestInvite", "true");
          //  this.router.navigate(['guest-invite']);
        }
        else if (data.event_state == "Past" && restricted) {
          this.router.navigate(['my-photos/' + localStorage.getItem('eventId')]);
        }
        else {
          this.router.navigate(['event-detail/true']);
        }
        // localStorage.setItem("openPopup", "true");
      } else {
        localStorage.setItem("isEventId", "false");
      }
      this.navigateToLogin();
    });
  }

  /**
   * Handles error responses by showing a snackbar and navigating to login.
   * @param data - The error response data.
   */
  private handleErrorResponse(data: any): void {
    this.bar.open(data.result, "Ok", {
      panelClass: "error-bar",
      duration: 3000,
    });
    localStorage.setItem("isEventId", "false");
    this.navigateToLogin();
  }

  /**
   * Handles errors from the event service.
   * @param error - The error object.
   */
  private handleError(error: { error: { result: string }; status: number }): void {
    // console.log(error); //__CONSOLE_LOG__
    this.bar.open(error.error.result, "Ok", {
      panelClass: "error-bar",
      duration: 3000,
    });

    if (error.status === 500) {
      SafeStorage.clearExceptPrefixes(['ph_']);
      localStorage.setItem("isEventId", "false");
      this.navigateToLogin();
    }

    if (error.status === 400) {
      this.activatedRoute.params.subscribe((params: any) => {
        if (params.id !== "null") {
          localStorage.setItem("eventId", params.id);
          localStorage.setItem("isEventId", "true");
        } else {
          localStorage.setItem("isEventId", "false");
        }
        this.navigateToLogin();
      });
    }
  }

  /**
   * Navigates to the login page.
   */
  private navigateToLogin(): void {
    this.router.navigateByUrl("/auth/login", { replaceUrl: true });
  }

  /**
   * Detects the platform (Android or iOS) and checks if the app is installed.
   * If the app is installed, redirects to the app. Otherwise, stays on the PWA site.
   */
  private detectPlatformAndRedirect(): void {
    // Deep linking disabled - using PWA only
    // Uncomment and configure the deep links below if you have native apps
    
    /*
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

    // Define deep links for Android and iOS
    const androidAppLink = "your-android-app-deep-link://";
    const iosAppLink = "your-ios-app-deep-link://";

    // Check if the user is on Android
    if (/android/i.test(userAgent)) {
      this.redirectToApp(androidAppLink);
    }
    // Check if the user is on iOS
    else if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
      this.redirectToApp(iosAppLink);
    }
    // If the platform is neither Android nor iOS, stay on the PWA site
    else {
      // console.log("User is on an unsupported platform. Staying on PWA site."); //__CONSOLE_LOG__
    }
    */
  }

  /**
   * Attempts to redirect to the app using a deep link.
   * If the app is not installed, the user stays on the PWA site.
   * @param appLink - The deep link for the app.
   */
  private redirectToApp(appLink: string): void {
    window.location.href = appLink;
    setTimeout(() => {
      // If the app is not installed, the user stays on the PWA site
      if (!document.hidden) {
        // console.log("App is not installed. Staying on PWA site."); //__CONSOLE_LOG__
      }
    }, 500); // Timeout to allow the app to open
  }
}